/**
 * Thin fetch wrapper for signals_app's REST surface.
 * Dev: Vite proxies /api → signals_app (see vite.config.ts).
 * Prod: same origin when Tauri wraps, or configurable via VITE_SIGNALS_URL.
 */

const BASE = import.meta.env.VITE_SIGNALS_URL ?? "";

type ApiQueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly (string | number | boolean)[];

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`${status}: ${detail}`);
  }
}

async function unwrap(resp: Response): Promise<unknown> {
  const contentType = resp.headers.get("content-type") ?? "";
  if (resp.ok) {
    if (resp.status === 204) return null;
    if (!contentType.includes("application/json")) {
      throw apiUnavailableError(resp);
    }
    return resp.json();
  }
  let detail = resp.statusText;
  try {
    const body = await resp.json();
    detail = body?.detail ?? JSON.stringify(body);
  } catch {
    detail = await resp.text().catch(() => resp.statusText);
  }
  throw new ApiError(resp.status, detail);
}

function apiUnavailableError(resp: Response): ApiError {
  const configuredBase = BASE || "same-origin /api";
  return new ApiError(
    503,
    `Signals API unavailable. UI is configured to call ${configuredBase}, but the response from ${resp.url} was not JSON. Start the Signals API runtime or configure the Cloudflare Tunnel/API URL.`,
  );
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, ApiQueryValue>,
): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        for (const item of v) url.searchParams.append(k, String(item));
      } else {
        url.searchParams.set(k, String(v));
      }
    }
  }
  const resp = await fetch(url.toString(), { cache: "no-cache", method: "GET" });
  return (await unwrap(resp)) as T;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return (await unwrap(resp)) as T;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await unwrap(resp)) as T;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await unwrap(resp)) as T;
}

export async function apiDelete(path: string): Promise<void> {
  const resp = await fetch(`${BASE}${path}`, { method: "DELETE" });
  await unwrap(resp);
}

// ── Response shapes (mirror signals_app Pydantic responses) ─────────

export interface SourceConfig {
  id: string;
  display_name: string;
  vendor: string | null;
  source_type: string | null;
  description: string | null;
  is_active: boolean;
  routing: string;
  confidence_tier: string;
  cadence: string;
  source_url: string | null;
  webhook_provider: string | null;
  deactivated_at: string | null;
  /** Operator pause (distinct from is_active). */
  is_schedule_paused: boolean;
  paused_at: string | null;
  /** Null + is_schedule_paused=true → indefinite pause. */
  paused_until: string | null;
  paused_reason: string | null;
  created_at: string;
  updated_at: string | null;
  config: Record<string, unknown> | null;
}

export interface WebhookSourceStatus {
  source_id: string;
  provider: string;
  active: boolean;
  endpoint_path: string;
  provider_endpoint_path: string;
  secret_configured: boolean;
  secret_available: boolean;
}

export interface WebhookConfig {
  id: string;
  vendor_id: string | null;
  display_name: string;
  provider: string;
  enabled: boolean;
  default_template_id: string | null;
  allowed_template_ids: string[];
  auth_config: Record<string, unknown>;
  parser_config: Record<string, unknown>;
  routing: string;
  confidence_tier: string;
  status: string;
  metadata: Record<string, unknown>;
  endpoint_path: string;
  provider_endpoint_path: string;
}

export interface WebhookFormatSpec {
  id: string;
  template_key: string;
  template_version: string;
  label: string;
  content_type: string;
  description: string;
  template: string;
  parser_mode: string;
}

export interface WebhookProviderSpec {
  provider: string;
  display_name: string;
  auth_description: string;
  formats: WebhookFormatSpec[];
}

export interface WebhookConfigCreateRequest {
  id: string;
  display_name: string;
  provider: string;
  vendor_id?: string | null;
  enabled?: boolean;
  default_template_id?: string | null;
  allowed_template_ids?: string[] | null;
  auth_config?: Record<string, unknown> | null;
  parser_config?: Record<string, unknown> | null;
  routing?: string;
  confidence_tier?: string;
  metadata?: Record<string, unknown> | null;
}

export interface WebhookConfigUpdateRequest {
  display_name?: string | null;
  provider?: string | null;
  vendor_id?: string | null;
  enabled?: boolean | null;
  default_template_id?: string | null;
  allowed_template_ids?: string[] | null;
  auth_config?: Record<string, unknown> | null;
  parser_config?: Record<string, unknown> | null;
  routing?: string | null;
  confidence_tier?: string | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface WebhookTemplate {
  id: string;
  provider: string;
  template_key: string;
  template_version: string;
  display_name: string;
  content_type: string;
  body: string;
  parser_mode: string;
  enabled: boolean;
  built_in: boolean;
  schema_version: string;
  metadata: Record<string, unknown>;
}

export interface WebhookTemplateCreateRequest {
  id: string;
  provider: string;
  template_key: string;
  template_version?: string;
  display_name: string;
  content_type?: string;
  body: string;
  parser_mode?: string;
  enabled?: boolean;
  metadata?: Record<string, unknown> | null;
}

export interface WebhookTemplateUpdateRequest {
  provider?: string | null;
  template_key?: string | null;
  template_version?: string | null;
  display_name?: string | null;
  content_type?: string | null;
  body?: string | null;
  parser_mode?: string | null;
  enabled?: boolean | null;
  metadata?: Record<string, unknown> | null;
}

export interface WebhookTemplateTestRequest {
  provider: string;
  body: string;
  content_type: string;
  webhook_config_id?: string | null;
  default_template_id?: string | null;
}

export interface WebhookTemplateTestResponse {
  provider: string;
  template: Record<string, unknown> | null;
  item_type: string | null;
  detected_symbols: string[];
  raw_text_preview: string;
  structured_payload: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
}

export interface PauseResponse {
  source_id: string;
  is_schedule_paused: boolean;
  paused_at: string | null;
  paused_until: string | null;
  paused_reason: string | null;
}

export interface SourceCreateRequest {
  id: string;
  display_name: string;
  vendor?: string | null;
  source_type?: string | null;
  routing?: string;
  confidence_tier?: string;
  cadence?: string;
  source_url?: string | null;
  description?: string | null;
  webhook_provider?: string | null;
  webhook_secret_ref?: string | null;
  is_active?: boolean;
  config?: Record<string, unknown> | null;
}

export interface SourceUpdateRequest {
  display_name?: string;
  vendor?: string | null;
  source_type?: string | null;
  routing?: string;
  confidence_tier?: string;
  cadence?: string;
  source_url?: string | null;
  description?: string | null;
  webhook_provider?: string | null;
  webhook_secret_ref?: string | null;
  is_active?: boolean;
  config?: Record<string, unknown> | null;
}

export interface InboxItem {
  id: string;
  source_id: string;
  item_type: string | null;
  detected_symbols: string[] | null;
  candidate_symbols: string[];
  candidate_count: number;
  status: string;
  item_timestamp: string | null;
  captured_at: string;
  page_url: string | null;
  is_manual: boolean;
  raw_text: string | null;
  markdown: string | null;
  structured_payload: Record<string, unknown> | null;
  dsl_text: string | null;
  source_metadata: Record<string, unknown> | null;
}

export interface InboxItemList {
  total: number;
  items: InboxItem[];
}

export interface SignalExtractionPayload {
  schema_version: string;
  extracted_at: string;
  inbox_item_ids: string[];
  model: string;
  summary: string | null;
  signals: Record<string, unknown>[];
  uncertainty: string[];
  signal_dsl: string | null;
}

export interface SignalExtractionBatchPayload {
  schema_version: string;
  extracted_at: string;
  requested_count: number;
  converted_count: number;
  skipped_count: number;
  failed_count: number;
  batch_count: number;
  payloads: SignalExtractionPayload[];
  skipped_ids: string[];
  errors: Array<{ inbox_item_ids: string[]; error: string }>;
}

export interface SignalCandidateItem {
  inbox_id: string;
  vendor_id: string | null;
  source_id: string | null;
  item_type: string | null;
  status: string;
  contribution_role: string;
  item_timestamp: string | null;
  captured_at: string;
  page_url: string | null;
  dsl_text: string | null;
  preview_text: string | null;
}

export interface SignalCandidate {
  candidate_id: string;
  vendor_id: string | null;
  subscription_id: string | null;
  correlation_window: string;
  symbol: string;
  underlying_symbols: string[];
  symbols: string[];
  option_contracts: Record<string, unknown>[];
  source_id: string | null;
  item_count: number;
  source_ids: string[];
  item_types: string[];
  statuses: string[];
  latest_item_timestamp: string | null;
  latest_captured_at: string;
  oldest_item_timestamp: string | null;
  oldest_captured_at: string;
  latest_action: string | null;
  latest_dsl_text: string | null;
  effective_dsl_text: string | null;
  structured_signal: Record<string, unknown> | null;
  items: SignalCandidateItem[];
}

export interface SignalCandidateList {
  total: number;
  items: SignalCandidate[];
}

export interface PublishValidationFinding {
  check: string;
  severity: string;
  message: string;
}

export interface PublishResult {
  target: string;
  candidate_id: string | null;
  symbol: string;
  outbox_id: string;
  outbox_status: string;
  validation_findings: PublishValidationFinding[];
  target_available: boolean;
  error_message: string | null;
}

export interface PublishResponse {
  results: PublishResult[];
}

export interface ConfigRestoreReport {
  added: string[];
  updated: string[];
  reactivated: string[];
  unchanged: string[];
  deactivated: string[];
}

export interface ConfigBackupRestoreResponse {
  restored: Record<string, number>;
}

export interface RunResponse {
  source_id: string;
  vendor: string;
  output_kind: "inbox_items";
  inbox_items_created: number;
  processing_status: "skipped" | "succeeded" | "partial" | "failed" | string;
  processing_requested_count: number;
  processing_converted_count: number;
  processing_failed_count: number;
  processing_error?: string | null;
}

export interface ClearCapturedContentResponse {
  source_id: string;
  inbox_items_deleted: number;
}

export interface ScrapePolicy {
  enabled: boolean;
  max_concurrency_per_vendor: number;
  min_seconds_between_vendor_fetches: number;
  min_seconds_between_source_runs: number;
  lock_stale_seconds: number;
}

export interface SignalAuditEvent {
  id: string;
  run_id: string;
  scraper_config_id?: string | null;
  vendor_id?: string | null;
  vendor_page_id?: string | null;
  event_type: string;
  status: string;
  severity?: string | null;
  actor_type?: string | null;
  actor_id?: string | null;
  trigger_kind: string | null;
  duration_ms: number | null;
  items_seen: number | null;
  items_saved: number | null;
  duplicates_skipped: number | null;
  error_type: string | null;
  error_message: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

export type SourceRuntimeMode = "poll" | "warm_context" | "warm_page" | "hybrid";

export interface SourceRuntime {
  mode: SourceRuntimeMode;
  supported_modes: SourceRuntimeMode[];
  config: {
    observer_strategy?: string;
    watchdog_interval_seconds?: number;
    reconcile_interval_seconds?: number;
    browser_restart_interval_minutes?: number;
    [key: string]: unknown;
  };
  browser_state_dir: string;
  storage_state_path: string | null;
  chrome_profile_dir: string | null;
  notes: string[];
}

export interface SourceOperations {
  source_id: string;
  cadence: string;
  supported_cadences: string[];
  runtime: SourceRuntime;
  worker: ContentScraperWorker;
  scrape_policy: ScrapePolicy;
  processing: SourceProcessing;
  recent_audit_events: SignalAuditEvent[];
}

export interface SourceProcessing {
  auto_extract: boolean;
  group_size: number;
}

export interface ContentScraperWorker {
  transport: string;
  service_url: string | null;
  configured: boolean;
  paused: boolean | null;
  scheduled: boolean | null;
  cadence: string | null;
  runtime_mode: string | null;
  source_url: string | null;
  callback_url: string | null;
  running: boolean;
  last_run_at: string | null;
  last_error: string | null;
  error: string | null;
}

export interface ContentScraperSyncResponse {
  source_id: string;
  worker: ContentScraperWorker;
  message: string;
}

export interface OperatorSourceSummary {
  id: string;
  display_name: string;
  vendor: string;
  cadence: string;
  is_active: boolean;
  is_schedule_paused: boolean;
  paused_at: string | null;
  paused_until: string | null;
  paused_reason: string | null;
}

export interface OperatorRuntimeSummary {
  mode: SourceRuntimeMode;
  browser_state_dir: string;
  storage_state_path: string | null;
  chrome_profile_dir: string | null;
}

export interface OperatorWorkerSummary {
  configured: boolean;
  paused: boolean | null;
  scheduled: boolean | null;
  cadence: string | null;
  service_url: string | null;
  running: boolean;
  last_run_at: string | null;
  last_error: string | null;
  error: string | null;
}

export interface OperatorSourceStatus {
  source: OperatorSourceSummary;
  health: "ok" | "warn" | "error" | "loading";
  recent_failure_count: number;
  last_run_at: string | null;
  next_run_at: string | null;
  runtime: OperatorRuntimeSummary;
  worker: OperatorWorkerSummary;
  last_audit_event: SignalAuditEvent | null;
}

export interface OperatorProcessorStatus {
  transport: string;
  service_url: string;
  reachable: boolean;
  running: boolean;
  active_run_kind: string | null;
  active_started_at: string | null;
  last_run_at: string | null;
  last_error: string | null;
  completed_runs: number;
  failed_runs: number;
  loop_enabled: boolean;
  loop_paused: boolean;
  loop_interval_seconds: number | null;
  loop_last_tick_at: string | null;
  loop_next_tick_at: string | null;
  loop_last_summary: Record<string, unknown>;
  pending_new_unstructured: number;
  pending_unstructured: number;
  active_unstructured: number;
  recent_events: SignalAuditEvent[];
  error: string | null;
}

export interface OperatorProcessorActionResponse {
  status: string;
  summary: Record<string, unknown> | null;
}

export interface OperatorStatusResponse {
  health: "ok" | "warn" | "error" | "loading";
  scraper_sources: number;
  active_schedules: number;
  missing_schedules: number;
  paused_sources: number;
  recent_failures: number;
  last_run_at: string | null;
  next_run_at: string | null;
  source_states: OperatorSourceStatus[];
  recent_audit_events: SignalAuditEvent[];
  processor: OperatorProcessorStatus;
}

export interface SourceOperationsUpdateRequest {
  cadence?: string;
  runtime_mode?: SourceRuntimeMode;
  runtime_config?: SourceRuntime["config"];
  scrape_policy?: Partial<ScrapePolicy>;
  processing?: Partial<SourceProcessing>;
}

// ── Vendor Pages (operator-facing tools, rooms, and KB candidates) ───

export interface VendorPage {
  id: string;
  label: string;
  nav_label: string | null;
  vendor: string;
  vendor_display_name: string;
  subscription: string;
  section: string;
  page_type: string;
  url: string;
  capabilities: VendorPageCapabilities;
  status: string;
  access_mode: string | null;
  iframe_url: string | null;
  focused_url: string | null;
  focus_url: string | null;
  content_type: string | null;
  notes: string | null;
  config: Record<string, unknown>;
}

export interface VendorPageCapabilities {
  open: boolean;
  embed: boolean;
  scrape: boolean;
  warm_page: boolean;
  monitor_activity: boolean;
  index: boolean;
}

export interface VendorPageUpsertRequest {
  id: string;
  label: string;
  nav_label?: string | null;
  vendor: string;
  vendor_display_name?: string | null;
  vendor_base_url?: string | null;
  subscription: string;
  section: string;
  page_type: string;
  url: string;
  capabilities: VendorPageCapabilities;
  status: string;
  access_mode?: string | null;
  iframe_url?: string | null;
  focused_url?: string | null;
  focus_url?: string | null;
  content_type?: string | null;
  notes?: string | null;
  config?: Record<string, unknown>;
}
